import axios from 'axios';

// OpenWeatherMap API configuration
const OWM_BASE_URL = 'https://api.openweathermap.org/data/2.5';
const OWM_API_KEY = 'b522f8878e498ec38590d443bd615dfb'; // OpenWeatherMap API key

/**
 * Fetch weather forecast for mission duration
 * @param {object} coordinates - Location coordinates { lat, lng }
 * @param {number} durationDays - Mission duration in days
 * @returns {Promise<object>} Weather forecast data
 */
export const fetchWeatherForecast = async (coordinates, durationDays = 5) => {
  try {
    // OpenWeatherMap free tier provides 5-day forecast
    const forecastDays = Math.min(durationDays, 5);

    const response = await axios.get(`${OWM_BASE_URL}/forecast`, {
      params: {
        lat: coordinates.lat,
        lon: coordinates.lng,
        appid: OWM_API_KEY,
        units: 'metric',
        cnt: forecastDays * 8 // 8 data points per day (3-hour intervals)
      }
    });

    return parseWeatherResponse(response.data, durationDays);
  } catch (error) {
    console.error('❌ Error fetching weather:', error);
    if (error.response) {
      console.error('Weather API Error:', error.response.data);
    }
    throw error;  // Throw error instead of generating mock data
  }
};



/**
 * Fetch current weather for a location
 * @param {object} coordinates - Location coordinates { lat, lng }
 * @returns {Promise<object>} Current weather data
 */
export const fetchCurrentWeather = async (coordinates) => {
  try {
    const response = await axios.get(`${OWM_BASE_URL}/weather`, {
      params: {
        lat: coordinates.lat,
        lon: coordinates.lng,
        appid: OWM_API_KEY,
        units: 'metric'
      }
    });

    return parseCurrentWeather(response.data);
  } catch (error) {
    console.error('❌ Error fetching current weather:', error);
    throw error;  // Throw error instead of generating mock data
  }
};

/**
 * Parse weather forecast response
 * @param {object} data - Raw response from OpenWeatherMap
 * @param {number} durationDays - Mission duration
 * @returns {object} Parsed weather data
 */
const parseWeatherResponse = (data, durationDays) => {
  const dailyForecasts = [];
  const hourlyData = data.list;

  // Group by day
  const groupedByDay = {};
  hourlyData.forEach(item => {
    const date = new Date(item.dt * 1000).toLocaleDateString();
    if (!groupedByDay[date]) {
      groupedByDay[date] = [];
    }
    groupedByDay[date].push(item);
  });

  // Calculate daily aggregates
  Object.entries(groupedByDay).forEach(([date, items]) => {
    const temps = items.map(i => i.main.temp);
    const humidity = items.map(i => i.main.humidity);
    const windSpeeds = items.map(i => i.wind.speed);
    const precipitation = items
      .map(i => (i.rain ? i.rain['3h'] || 0 : 0) + (i.snow ? i.snow['3h'] || 0 : 0))
      .reduce((a, b) => a + b, 0);

    // Get most common weather condition
    const conditions = items.map(i => i.weather[0].main);
    const mostCommonCondition = conditions.sort((a, b) =>
      conditions.filter(v => v === a).length - conditions.filter(v => v === b).length
    ).pop();

    const weatherIcon = items[Math.floor(items.length / 2)].weather[0].icon;

    dailyForecasts.push({
      date: new Date(items[0].dt * 1000),
      tempMin: Math.round(Math.min(...temps)),
      tempMax: Math.round(Math.max(...temps)),
      tempAvg: Math.round(temps.reduce((a, b) => a + b, 0) / temps.length),
      humidity: Math.round(humidity.reduce((a, b) => a + b, 0) / humidity.length),
      windSpeed: Math.round(windSpeeds.reduce((a, b) => a + b, 0) / windSpeeds.length),
      precipitation: Math.round(precipitation * 10) / 10,
      condition: mostCommonCondition,
      description: items[Math.floor(items.length / 2)].weather[0].description,
      icon: weatherIcon,
      visibility: Math.round(items[0].visibility / 1000), // km
      pressure: items[0].main.pressure,
      hourlyData: items
    });
  });

  // Calculate mission-wide aggregates
  const missionStats = calculateMissionWeatherStats(dailyForecasts);

  return {
    daily: dailyForecasts.slice(0, durationDays),
    missionStats,
    location: data.city
  };
};

/**
 * Parse current weather response
 * @param {object} data - Raw current weather data
 * @returns {object} Parsed current weather
 */
const parseCurrentWeather = (data) => {
  return {
    temp: Math.round(data.main.temp),
    feelsLike: Math.round(data.main.feels_like),
    tempMin: Math.round(data.main.temp_min),
    tempMax: Math.round(data.main.temp_max),
    humidity: data.main.humidity,
    pressure: data.main.pressure,
    windSpeed: Math.round(data.wind.speed),
    windDirection: data.wind.deg,
    visibility: Math.round(data.visibility / 1000),
    condition: data.weather[0].main,
    description: data.weather[0].description,
    icon: data.weather[0].icon,
    clouds: data.clouds.all,
    sunrise: new Date(data.sys.sunrise * 1000),
    sunset: new Date(data.sys.sunset * 1000)
  };
};

/**
 * Calculate mission-wide weather statistics
 * @param {array} dailyForecasts - Daily forecast data
 * @returns {object} Mission weather stats
 */
const calculateMissionWeatherStats = (dailyForecasts) => {
  if (!dailyForecasts || dailyForecasts.length === 0) {
    return null;
  }

  const temps = dailyForecasts.flatMap(d => [d.tempMin, d.tempMax]);
  const totalPrecipitation = dailyForecasts.reduce((sum, d) => sum + d.precipitation, 0);
  const avgWindSpeed = Math.round(
    dailyForecasts.reduce((sum, d) => sum + d.windSpeed, 0) / dailyForecasts.length
  );
  const avgHumidity = Math.round(
    dailyForecasts.reduce((sum, d) => sum + d.humidity, 0) / dailyForecasts.length
  );

  // Count weather conditions
  const conditions = dailyForecasts.map(d => d.condition);
  const conditionCounts = {};
  conditions.forEach(c => {
    conditionCounts[c] = (conditionCounts[c] || 0) + 1;
  });
  const dominantCondition = Object.entries(conditionCounts)
    .sort((a, b) => b[1] - a[1])[0][0];

  return {
    tempMin: Math.round(Math.min(...temps)),
    tempMax: Math.round(Math.max(...temps)),
    tempRange: `${Math.round(Math.min(...temps))}°C - ${Math.round(Math.max(...temps))}°C`,
    totalPrecipitation: Math.round(totalPrecipitation * 10) / 10,
    avgWindSpeed,
    avgHumidity,
    dominantCondition,
    rainyDays: dailyForecasts.filter(d => d.precipitation > 0).length,
    clearDays: dailyForecasts.filter(d => d.condition === 'Clear').length
  };
};

/**
 * Get terrain-specific weather factors
 * @param {object} weather - Weather data
 * @param {string} terrainType - Type of terrain (desert, mountain, etc.)
 * @returns {object} Terrain-specific weather analysis
 */
export const getTerrainWeatherFactors = (weather, terrainType = 'mixed') => {
  const factors = {
    terrainType,
    warnings: [],
    visibility: 'Good',
    traversability: 'Good',
    recommendations: []
  };

  // Desert-specific factors
  if (terrainType.toLowerCase().includes('desert')) {
    if (weather.tempMax > 35) {
      factors.warnings.push('Extreme heat - High risk of heat exhaustion');
      factors.traversability = 'Challenging';
    }
    if (weather.windSpeed > 20) {
      factors.warnings.push('High winds - Sandstorm risk');
      factors.visibility = 'Poor';
    }
    if (weather.humidity < 20) {
      factors.warnings.push('Very low humidity - Dehydration risk');
    }
    factors.recommendations.push('Carry extra water');
    factors.recommendations.push('Plan travel during cooler hours');
  }

  // Mountain-specific factors
  if (terrainType.toLowerCase().includes('mountain')) {
    if (weather.tempMin < 0) {
      factors.warnings.push('Below freezing temperatures - Ice hazard');
      factors.traversability = 'Difficult';
    }
    if (weather.windSpeed > 15) {
      factors.warnings.push('Strong winds at elevation');
    }
    if (weather.precipitation > 5) {
      factors.warnings.push('Heavy precipitation - Avalanche/landslide risk');
      factors.traversability = 'Dangerous';
    }
    factors.recommendations.push('Monitor weather closely');
    factors.recommendations.push('Check avalanche forecasts');
  }

  // Forest-specific factors
  if (terrainType.toLowerCase().includes('forest')) {
    if (weather.precipitation > 10) {
      factors.warnings.push('Heavy rain - Muddy terrain');
      factors.traversability = 'Difficult';
    }
    if (weather.humidity > 80) {
      factors.warnings.push('High humidity - Reduced equipment performance');
    }
  }

  // General visibility factors
  if (weather.condition === 'Fog' || weather.condition === 'Mist') {
    factors.visibility = 'Poor';
    factors.warnings.push('Limited visibility conditions');
  }
  if (weather.condition === 'Rain' || weather.condition === 'Snow') {
    factors.visibility = 'Reduced';
    if (weather.precipitation > 5) {
      factors.traversability = 'Difficult';
    }
  }

  return factors;
};

/**
 * Get weather icon URL
 * @param {string} iconCode - OpenWeatherMap icon code
 * @returns {string} Icon URL
 */
export const getWeatherIconUrl = (iconCode) => {
  return `https://openweathermap.org/img/wn/${iconCode}@2x.png`;
};

/**
 * Get weather condition color
 * @param {string} condition - Weather condition
 * @returns {string} Tailwind color class
 */
export const getWeatherConditionColor = (condition) => {
  const colorMap = {
    'Clear': 'text-yellow-400',
    'Clouds': 'text-gray-400',
    'Rain': 'text-blue-400',
    'Drizzle': 'text-blue-300',
    'Thunderstorm': 'text-purple-400',
    'Snow': 'text-cyan-200',
    'Mist': 'text-gray-300',
    'Fog': 'text-gray-300',
    'Dust': 'text-orange-300',
    'Sand': 'text-orange-300'
  };
  return colorMap[condition] || 'text-gray-400';
};

/**
 * Detect terrain type from route coordinates (simplified)
 * @param {array} coordinates - Route coordinates
 * @returns {string} Terrain type
 */
export const detectTerrainType = (coordinates) => {
  if (!coordinates || coordinates.length === 0) return 'mixed';

  const elevations = coordinates.map(c => c.elevation);
  const avgElevation = elevations.reduce((a, b) => a + b, 0) / elevations.length;
  const elevationRange = Math.max(...elevations) - Math.min(...elevations);

  if (elevationRange > 1000) return 'mountainous';
  if (avgElevation > 2000) return 'mountainous';
  if (avgElevation < 500 && elevationRange < 100) return 'desert';
  return 'mixed';
};
